import { useState, useEffect, useCallback } from 'react';
import {
  X, Plus, Loader2, AlertCircle, CheckCircle, Wifi,
  Server, Camera, Router, Network, HardDrive, Monitor,
  Activity, Printer, Phone, Shield, Zap,
  FileText, Tag
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';

const DEVICE_TYPES = [
  { value: 'CAMERA', label: 'Camera', icon: Camera },
  { value: 'NVR', label: 'NVR', icon: HardDrive },
  { value: 'DVR', label: 'DVR', icon: HardDrive },
  { value: 'VMS_SERVER', label: 'VMS Server', icon: Server },
  { value: 'ACCESS_CONTROLLER', label: 'Access Controller', icon: Shield },
  { value: 'INTERCOM', label: 'Intercom', icon: Phone },
  { value: 'SWITCH', label: 'Switch', icon: Network },
  { value: 'ROUTER', label: 'Router', icon: Router },
  { value: 'ACCESS_POINT', label: 'Access Point', icon: Wifi },
  { value: 'FIREWALL', label: 'Firewall', icon: Shield },
  { value: 'SERVER', label: 'Server', icon: Server },
  { value: 'WORKSTATION', label: 'Workstation', icon: Monitor },
  { value: 'NAS', label: 'NAS', icon: HardDrive },
  { value: 'UPS', label: 'UPS', icon: Zap },
  { value: 'PDU', label: 'PDU', icon: Zap },
  { value: 'IOT_DEVICE', label: 'IoT Device', icon: Activity },
  { value: 'PRINTER', label: 'Printer', icon: Printer },
  { value: 'OTHER', label: 'Other', icon: Monitor },
];

const MONITORING_PROFILES = [
  { value: 'PING', label: 'ICMP Ping', desc: 'Basic reachability check' },
  { value: 'HTTP_CHECK', label: 'HTTP Health Check', desc: 'Check HTTP/HTTPS endpoint' },
  { value: 'RTSP_CHECK', label: 'RTSP Stream Check', desc: 'Verify RTSP stream availability' },
  { value: 'API_HEALTH', label: 'API Health', desc: 'VMS/Access control API check' },
  { value: 'SNMP_POLL', label: 'SNMP Poll', desc: 'Query SNMP OIDs for status' },
  { value: 'WMI', label: 'WMI', desc: 'Windows Management Instrumentation' },
];

const POLL_INTERVALS = [
  { value: 15, label: '15 seconds' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
];

const ALERT_THRESHOLDS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 1800, label: '30 minutes' },
];

const isValidIP = (ip) => {
  if (!ip) return false;
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every(p => {
    const num = parseInt(p, 10);
    return !isNaN(num) && num >= 0 && num <= 255 && String(num) === p;
  });
};

const MdAddDeviceModal = ({ isOpen, onClose, onDeviceAdded, existingGroups = [] }) => {
  const [form, setForm] = useState({
    name: '', ipAddress: '', macAddress: '', hostname: '',
    deviceType: 'UNKNOWN', manufacturer: '', model: '',
    monitoringProfile: 'PING', pollIntervalSeconds: 30,
    alertThresholdSeconds: 120, group: '', notes: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ipStatus, setIpStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: '', ipAddress: '', macAddress: '', hostname: '',
        deviceType: 'UNKNOWN', manufacturer: '', model: '',
        monitoringProfile: 'PING', pollIntervalSeconds: 30,
        alertThresholdSeconds: 120, group: '', notes: '',
      });
      setErrors({});
      setIpStatus(null);
      setShowAdvanced(false);
    }
  }, [isOpen]);

  const autoName = useCallback(() => {
    if (!form.name && form.ipAddress && isValidIP(form.ipAddress)) {
      const type = DEVICE_TYPES.find(t => t.value === form.deviceType);
      const label = type && type.value !== 'UNKNOWN' ? type.label : 'Device';
      setForm(f => ({ ...f, name: `${label} - ${f.ipAddress}` }));
    }
  }, [form.name, form.ipAddress, form.deviceType]);

  const suggestProfile = (deviceType) => {
    const map = {
      CAMERA: 'RTSP_CHECK', NVR: 'API_HEALTH', DVR: 'API_HEALTH',
      VMS_SERVER: 'API_HEALTH', ACCESS_CONTROLLER: 'API_HEALTH',
      SWITCH: 'SNMP_POLL', ROUTER: 'SNMP_POLL', FIREWALL: 'SNMP_POLL',
      SERVER: 'PING', WORKSTATION: 'PING',
      NAS: 'SNMP_POLL', UPS: 'SNMP_POLL', PDU: 'SNMP_POLL',
    };
    return map[deviceType] || 'PING';
  };

  const handleDeviceTypeChange = (deviceType) => {
    setForm(f => ({ ...f, deviceType, monitoringProfile: suggestProfile(deviceType) }));
  };

  const validate = () => {
    const errs = {};
    if (!form.ipAddress) errs.ipAddress = 'IP address is required';
    else if (!isValidIP(form.ipAddress)) errs.ipAddress = 'Invalid IP address format';
    if (!form.name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const [showUnreachableConfirm, setShowUnreachableConfirm] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  const buildBody = () => {
    const body = {
      name: form.name.trim(),
      ipAddress: form.ipAddress.trim(),
      deviceType: form.deviceType,
      monitoringProfile: form.monitoringProfile,
      pollIntervalSeconds: form.pollIntervalSeconds,
      alertThresholdSeconds: form.alertThresholdSeconds,
      source: 'MANUAL',
    };
    if (form.macAddress.trim()) body.macAddress = form.macAddress.trim();
    if (form.hostname.trim()) body.hostname = form.hostname.trim();
    if (form.manufacturer.trim()) body.manufacturer = form.manufacturer.trim();
    if (form.model.trim()) body.model = form.model.trim();
    if (form.group.trim()) body.group = form.group.trim();
    if (form.notes.trim()) body.notes = form.notes.trim();
    return body;
  };

  const addDevice = async () => {
    try {
      const res = await fetchWithTenant('/api/md/monitored', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        if (res.status === 409) {
          setErrors({ ipAddress: 'A device with this IP is already being monitored' });
          setIpStatus('duplicate');
        } else if (res.status === 403) {
          toast.error(data.error || 'License limit reached');
        } else {
          toast.error(data.error || 'Failed to add device');
        }
        return;
      }
      toast.success(`${form.name} added to monitoring`);
      onDeviceAdded?.(data.device);
      onClose();
    } catch (err) {
      console.error('Error adding device:', err);
      toast.error('Failed to add device');
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setShowUnreachableConfirm(false);
    try {
      // Test connection first
      setIpStatus('checking');
      const testRes = await fetchWithTenant('/api/md/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ipAddress: form.ipAddress.trim() }),
      });
      const testData = await testRes.json();

      if (testData.reachable) {
        setIpStatus('reachable');
        setConnectionResult(testData);
        await addDevice();
      } else {
        setIpStatus('unreachable');
        setConnectionResult(testData);
        setShowUnreachableConfirm(true);
      }
    } catch (err) {
      // If ping test fails, still allow adding
      setIpStatus('unreachable');
      setShowUnreachableConfirm(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceAdd = async () => {
    setSubmitting(true);
    setShowUnreachableConfirm(false);
    try {
      await addDevice();
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !submitting) { e.preventDefault(); handleSubmit(); }
    if (e.key === 'Escape') onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content md-add-device-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="modal-header">
          <div className="modal-header-title">
            <Plus size={20} />
            <h3>Add Device to Monitoring</h3>
          </div>
          <button className="modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body md-add-device-body">
          <div className="md-add-form-row">
            <div className={`md-add-form-group ${errors.ipAddress ? 'has-error' : ''}`}>
              <label>IP Address <span className="required">*</span></label>
              <div className="md-add-input-with-status">
                <input type="text" placeholder="192.168.1.100" value={form.ipAddress}
                  onChange={(e) => { setForm(f => ({ ...f, ipAddress: e.target.value })); setErrors(er => ({ ...er, ipAddress: null })); setIpStatus(null); }}
                  onBlur={autoName} autoFocus />
                {ipStatus === 'checking' && <Loader2 size={16} className="spin md-add-ip-icon" />}
                {ipStatus === 'reachable' && <CheckCircle size={16} className="md-add-ip-icon success" />}
                {ipStatus === 'unreachable' && <AlertCircle size={16} className="md-add-ip-icon warning" />}
                {ipStatus === 'duplicate' && <AlertCircle size={16} className="md-add-ip-icon error" />}
              </div>
              {errors.ipAddress && <span className="md-add-error">{errors.ipAddress}</span>}
            </div>
            <div className={`md-add-form-group ${errors.name ? 'has-error' : ''}`}>
              <label>Device Name <span className="required">*</span></label>
              <input type="text" placeholder="Lobby Camera 1" value={form.name}
                onChange={(e) => { setForm(f => ({ ...f, name: e.target.value })); setErrors(er => ({ ...er, name: null })); }} />
              {errors.name && <span className="md-add-error">{errors.name}</span>}
            </div>
          </div>

          <div className="md-add-form-group">
            <label>Device Type</label>
            <div className="md-add-type-grid">
              {DEVICE_TYPES.slice(0, 12).map(({ value, label, icon: Icon }) => (
                <button key={value} type="button"
                  className={`md-add-type-card ${form.deviceType === value ? 'active' : ''}`}
                  onClick={() => handleDeviceTypeChange(value)}>
                  <Icon size={18} /><span>{label}</span>
                </button>
              ))}
            </div>
            {DEVICE_TYPES.length > 12 && (
              <div className="md-add-type-more">
                <select value={DEVICE_TYPES.slice(0, 12).some(t => t.value === form.deviceType) ? '' : form.deviceType}
                  onChange={(e) => { if (e.target.value) handleDeviceTypeChange(e.target.value); }}>
                  <option value="">More types...</option>
                  {DEVICE_TYPES.slice(12).map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="md-add-form-row md-add-form-row-3">
            <div className="md-add-form-group">
              <label>Monitoring Profile</label>
              <select value={form.monitoringProfile} onChange={(e) => setForm(f => ({ ...f, monitoringProfile: e.target.value }))}>
                {MONITORING_PROFILES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
              <span className="md-add-hint">{MONITORING_PROFILES.find(p => p.value === form.monitoringProfile)?.desc}</span>
            </div>
            <div className="md-add-form-group">
              <label>Poll Interval</label>
              <select value={form.pollIntervalSeconds} onChange={(e) => setForm(f => ({ ...f, pollIntervalSeconds: parseInt(e.target.value) }))}>
                {POLL_INTERVALS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="md-add-form-group">
              <label>Alert Threshold</label>
              <select value={form.alertThresholdSeconds} onChange={(e) => setForm(f => ({ ...f, alertThresholdSeconds: parseInt(e.target.value) }))}>
                {ALERT_THRESHOLDS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="md-add-form-group">
            <label><Tag size={14} /> Group</label>
            <input type="text" placeholder="e.g. Building A, Lobby Cameras, Server Room" value={form.group}
              onChange={(e) => setForm(f => ({ ...f, group: e.target.value }))} list="md-add-groups-list" />
            {existingGroups.length > 0 && (
              <datalist id="md-add-groups-list">
                {existingGroups.map(g => <option key={g} value={g} />)}
              </datalist>
            )}
          </div>

          <button type="button" className="md-add-advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
            {showAdvanced ? '\u25BE Hide' : '\u25B8 Show'} advanced fields
          </button>

          {showAdvanced && (
            <div className="md-add-advanced">
              <div className="md-add-form-row">
                <div className="md-add-form-group">
                  <label>MAC Address</label>
                  <input type="text" placeholder="AA:BB:CC:DD:EE:FF" value={form.macAddress}
                    onChange={(e) => setForm(f => ({ ...f, macAddress: e.target.value }))} />
                </div>
                <div className="md-add-form-group">
                  <label>Hostname</label>
                  <input type="text" placeholder="device.local" value={form.hostname}
                    onChange={(e) => setForm(f => ({ ...f, hostname: e.target.value }))} />
                </div>
              </div>
              <div className="md-add-form-row">
                <div className="md-add-form-group">
                  <label>Manufacturer</label>
                  <input type="text" placeholder="e.g. Hanwha, Axis, Cisco" value={form.manufacturer}
                    onChange={(e) => setForm(f => ({ ...f, manufacturer: e.target.value }))} />
                </div>
                <div className="md-add-form-group">
                  <label>Model</label>
                  <input type="text" placeholder="e.g. XND-6080V" value={form.model}
                    onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))} />
                </div>
              </div>
              <div className="md-add-form-group">
                <label><FileText size={14} /> Notes</label>
                <textarea placeholder="Optional notes about this device..." value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
              </div>
            </div>
          )}
        </div>

        {/* Unreachable confirmation */}
        {showUnreachableConfirm && (
          <div className="md-add-unreachable-banner">
            <div className="md-add-unreachable-content">
              <AlertCircle size={20} />
              <div>
                <strong>Device not reachable</strong>
                <p>{form.ipAddress} did not respond to ping. The device may be offline, behind a firewall, or ICMP may be blocked.</p>
              </div>
            </div>
            <div className="md-add-unreachable-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowUnreachableConfirm(false)}>
                Go Back
              </button>
              <button className="btn btn-accent btn-sm" onClick={handleForceAdd} disabled={submitting}>
                {submitting ? <><Loader2 size={16} className="spin" /> Adding...</> : 'Add Anyway'}
              </button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-accent" onClick={handleSubmit} disabled={submitting || !form.ipAddress || !form.name || showUnreachableConfirm}>
            {submitting ? (<><Loader2 size={16} className="spin" /> Testing...</>) : (<><Plus size={16} /> Add to Monitoring</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MdAddDeviceModal;
